/**
 * Queries that answer *why*, for interfaces and the tutorial.
 *
 * Movegen answers "what may I play". That is enough to run a game and not nearly enough
 * to show one. RULES.md §4.1 puts it plainly: whether a given one-cell pawn-takes-pawn is
 * available depends on a bow the player has to notice, and the interface should show the
 * reason rather than leaving it as an unexplained exception. The 2023 client rendered a
 * bow shot and an ordinary step identically — both a slightly lighter hex — so the game's
 * signature mechanic was invisible in its own interface.
 *
 * Nothing here decides a rule. `describeBow` asks `hasBow` whether the bow is live and
 * only then works out which cells made it so, which is why a renderer built on this
 * cannot drift from the generator.
 */

import { type Board, OFF_BOARD } from './board.ts';
import { DIRECTION_COUNT } from './hex.ts';
import { hasBow, isAttackedBy, legalMoves } from './movegen.ts';
import { type FastPosition, isKing, ownerOf } from './position.ts';
import type { Move, PlayerIndex } from './types.ts';

/**
 * A live bow, and the cells that make it live.
 *
 * `flanks` are the two friendly pieces forming the elbow, `behind` is the empty cell
 * opposite the launch, and `ray` is every cell the glider may actually land on, in flight
 * order.
 *
 * `ray` holds **legal** destinations, not merely the geometric line. The two differ: a
 * bow can be perfectly well formed while the piece at its vertex is pinned, and a board
 * that drew the geometric ray would offer shots the server then refuses. A property test
 * pins the two together across random play.
 */
export interface BowShot {
  readonly direction: number;
  readonly flanks: readonly [number, number];
  readonly behind: number;
  readonly ray: readonly number[];
  /** The enemy piece at the end of the ray, if it can be taken. */
  readonly captures: number | null;
}

/**
 * The bow firing from `from` in `dir`, or `undefined` if there is not one the player to
 * move may fire. Describes shots for the side to move only, since legality is only
 * defined for them.
 */
export function describeBow(
  pos: FastPosition,
  from: number,
  dir: number,
): BowShot | undefined {
  return buildShot(pos, from, dir, destinationsFrom(pos, from));
}

/** Every bow the piece on `from` can fire, in direction order. */
export function describeBows(pos: FastPosition, from: number): BowShot[] {
  const reachable = destinationsFrom(pos, from);
  const shots: BowShot[] = [];
  for (let dir = 0; dir < DIRECTION_COUNT; dir++) {
    const shot = buildShot(pos, from, dir, reachable);
    if (shot) shots.push(shot);
  }
  return shots;
}

/** Legal `movePiece` destinations from a cell, as a set. */
function destinationsFrom(pos: FastPosition, from: number): Set<number> {
  return new Set(
    legalMovesFrom(pos, from)
      .filter((m) => m.type === 'movePiece')
      .map((m) => m.toIndex),
  );
}

function buildShot(
  pos: FastPosition,
  from: number,
  dir: number,
  reachable: ReadonlySet<number>,
): BowShot | undefined {
  const piece = pos.cells[from];
  if (piece === 0) return undefined;

  const player = ownerOf(piece);
  if (!hasBow(pos, from, dir, player)) return undefined;

  const { board, cells } = pos;
  const ray: number[] = [];
  let captures: number | null = null;

  for (let cell = board.neighborOf(from, dir); cell !== OFF_BOARD;) {
    const occupant = cells[cell];
    if (occupant !== 0) {
      // A glider stops before a friend and on an enemy, capturing it (§4).
      if (ownerOf(occupant) !== player && reachable.has(cell)) {
        ray.push(cell);
        captures = cell;
      }
      break;
    }
    if (reachable.has(cell)) ray.push(cell);
    cell = board.neighborOf(cell, dir);
  }

  // A bow whose every destination is illegal is not a shot anyone can take, and drawing
  // it would be a promise the rules will not keep.
  if (ray.length === 0) return undefined;

  return {
    direction: dir,
    flanks: [
      board.neighborOf(from, (dir + 2) % DIRECTION_COUNT),
      board.neighborOf(from, (dir + 4) % DIRECTION_COUNT),
    ],
    behind: board.neighborOf(from, (dir + 3) % DIRECTION_COUNT),
    ray,
    captures,
  };
}

/**
 * The legal moves that start at `from`, which is what a board needs the moment a piece is
 * picked up. Spawns are included: they start at the king that places them.
 */
export function legalMovesFrom(pos: FastPosition, from: number): Move[] {
  return legalMoves(pos).filter((m) =>
    (m.type === 'movePiece' || m.type === 'spawnPiece') && m.fromIndex === from
  );
}

/**
 * Kings standing in an enemy's line of fire, whoever is to move.
 *
 * Not the same question as `isInCheck`, which asks only about the side to move and only
 * about their *last* king. A board wants to mark every king that is under threat, because
 * with two kings alive losing one is a legal risk to take (§6) and the player should be
 * able to see the risk they are taking.
 */
export function threatenedKings(pos: FastPosition): number[] {
  const cells: number[] = [];
  for (let i = 0; i < pos.cells.length; i++) {
    const piece = pos.cells[i];
    if (piece === 0 || !isKing(piece)) continue;
    const enemy = (ownerOf(piece) ^ 1) as PlayerIndex;
    if (isAttackedBy(pos, i, enemy)) cells.push(i);
  }
  return cells;
}

/** Pixel centre of a cell, for laying a board out. */
export const cellCentre = (board: Board, index: number): { x: number; y: number } => ({
  x: board.xy[index * 2],
  y: board.xy[index * 2 + 1],
});

/**
 * Every piece of `player`'s that could capture on `cell`, in cell order.
 *
 * The counting form of `isAttackedBy`, which stops at the first attacker because that is
 * all legality ever needs to know. An evaluation wants the number: a king with two
 * attackers and one flight square is in a different position from one with a single
 * attacker, and a boolean cannot tell them apart.
 *
 * Deliberately a second traversal rather than a refactor of `isAttackedBy` into a
 * callback both share. That function is the innermost loop of move generation — it runs
 * once per candidate move — and making its call site polymorphic to save duplication here
 * would be paying in the hottest place in the engine for tidiness in one of the coldest.
 * The two are pinned together by a property test instead, the same way `describeBow` is
 * pinned to the generator.
 */
export function attackersOf(
  pos: FastPosition,
  cell: number,
  player: PlayerIndex,
): number[] {
  const { board, cells } = pos;
  const found: number[] = [];

  for (let dir = 0; dir < DIRECTION_COUNT; dir++) {
    const n = board.neighborOf(cell, dir);
    if (n === OFF_BOARD) continue;
    const c = cells[n];
    if (c !== 0 && ownerOf(c) === player) found.push(n);
  }

  // Gliders, found by walking each ray backwards to the first occupied cell — the only
  // one that could be shooting down it.
  for (let dir = 0; dir < DIRECTION_COUNT; dir++) {
    const back = (dir + 3) % DIRECTION_COUNT;
    let at = board.neighborOf(cell, back);

    while (at !== OFF_BOARD) {
      const c = cells[at];
      if (c !== 0) {
        // A glider adjacent to the cell is already in the list from the pass above; the
        // shot and the step are the same move, and it must not be counted twice.
        if (
          ownerOf(c) === player && hasBow(pos, at, dir, player) && !found.includes(at)
        ) {
          found.push(at);
        }
        break;
      }
      at = board.neighborOf(at, back);
    }
  }

  return found.sort((a, b) => a - b);
}
